import Typography from '@components/v2/ui/Typography/Typography';
import { Box, Stack } from '@mui/material';
import LangEnum from '../../../../../lang/LangEnum';
import { useTranslation } from '../../../../translation/TranslationContext';
import OrderTags from '../../../list/OrderTags';
import { Section, SectionPendencyEnum } from '../../orderProducts.types';

type OrderProductGroupTitleVariant = 'section' | 'parentGroup';

interface OrderProductGroupTitleProps {
  section: Section;
  variant?: OrderProductGroupTitleVariant;
}

const OrderProductGroupTitle = ({ section, variant = 'section' }: OrderProductGroupTitleProps) => {
  const { translate } = useTranslation();

  const {
    availability,
    deliveryWindow,
    collection,
    category,
    subCategory,
    colorClassification,
    pendency,
    productTitle,
    groupTitle,
    groupTitleFallbackEntity,
    parentGroupKey,
    parentGroupTitle,
    parentGroupTitleFallbackEntity,
  } = section;

  const isParentGroup = variant === 'parentGroup';

  const subCategoryLabel = subCategory?.parent
    ? `${subCategory.parent.name} - ${subCategory.name}`
    : subCategory?.name;

  const pendencyLabel =
    pendency &&
    translate(
      pendency === SectionPendencyEnum.WITH_PENDENCIES
        ? LangEnum.ORDER_PRODUCTS_SECTION_ITEMS_WITH_PENDENCIES_LABEL
        : LangEnum.ORDER_PRODUCTS_SECTION_ITEMS_WITHOUT_PENDENCIES_LABEL,
    );

  const translateWithoutEntity = (entity?: LangEnum) =>
    entity && translate(LangEnum.GLOBAL_WITHOUT, { entity: translate(entity) });

  const groupLabel = groupTitle || translateWithoutEntity(groupTitleFallbackEntity);
  const parentGroupLabel =
    parentGroupTitle || translateWithoutEntity(parentGroupTitleFallbackEntity);

  const sectionLabels = [
    collection?.name,
    category?.name,
    subCategoryLabel,
    colorClassification?.name,
    pendencyLabel,
    productTitle,
    groupLabel,
  ];

  const labels = (isParentGroup ? [parentGroupLabel] : sectionLabels).filter(
    (label): label is string => !!label,
  );

  const hasAvailabilityTag = !!availability && (isParentGroup || !parentGroupKey);

  return (
    <Stack direction="row" sx={{ minWidth: 0, gap: 1, alignItems: 'center' }}>
      {hasAvailabilityTag && (
        <Box sx={{ minWidth: 0 }}>
          <OrderTags availabilities={[availability]} deliveryWindow={deliveryWindow} />
        </Box>
      )}

      {labels.map((label, index) => (
        <Typography key={`${index}-${label}`} variant="body2" noWrap title={label}>
          {label}
        </Typography>
      ))}
    </Stack>
  );
};

export default OrderProductGroupTitle;
